import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Trash, Plus, Settings, Box, Truck, Link as LinkIcon, Unlink } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { PackagingManagementModal } from '../modals/PackagingManagementModal';
import { PalletResultsTable } from '../PalletResultsTable';
import { LiveTrackingDashboard } from '../widgets/LiveTrackingDashboard';
import { SearchableSelect } from '../ui/SearchableSelect';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const PackagingLogisticsTab = () => {
    const {
        packagingProperties,
        availablePackagingProperties,
        addPackagingProperty,
        deletePackagingProperty,
        updatePackagingProperty,
        canEditContent,
        canManageSettings,
        setLeftWidget,
        setMiddleWidget,
        setRightWidget,
        isPalletMode,
        setIsPalletMode,
        palletMiddleWidget,
        setPalletMiddleWidget,
        setPalletRightWidget,
        fetchPackagingProperties,
        productData,
        activeCharge,
        updateCharge // To save source selection
    } = usePIM();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);

    // Modal State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProperties, setSelectedProperties] = useState([]);

    // Inheritance State
    const [sourceProduct, setSourceProduct] = useState(null);
    const [sourceCharge, setSourceCharge] = useState(null);
    const [availableSourceCharges, setAvailableSourceCharges] = useState([]);
    const [accessoryProducts, setAccessoryProducts] = useState([]);

    // Fetch Accessory Products
    useEffect(() => {
        fetch('/api/products?is_accessory=true', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
            .then(res => res.json())
            .then(data => setAccessoryProducts(data))
            .catch(err => console.error("Failed to fetch accessory products", err));
    }, []);

    // Initialize Source Selection from Active Charge
    useEffect(() => {
        if (activeCharge) {
            setSourceProduct(activeCharge.packaging_source_product_id);
            setSourceCharge(activeCharge.packaging_source_charge_id);
        }
    }, [activeCharge]);

    // Fetch charges when source product changes
    useEffect(() => {
        if (sourceProduct) {
            // Find the selected product in accessoryProducts to get its SKU
            const selectedProd = accessoryProducts.find(p => p.id === sourceProduct);

            if (selectedProd) {
                // Corrected API endpoint: /api/charges?sku={sku}
                fetch(`/api/charges?sku=${selectedProd.sku}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                })
                    .then(res => res.json())
                    .then(data => setAvailableSourceCharges(data))
                    .catch(err => console.error("Failed to fetch source charges:", err));
            } else {
                // If not found in accessory list, we can't easily fetch charges by SKU without an ID lookup.
                // For now, we clear the list to avoid confusion.
                setAvailableSourceCharges([]);
            }
        } else {
            setAvailableSourceCharges([]);
            setSourceCharge(null);
        }
    }, [sourceProduct, accessoryProducts]);

    const handleSourceChange = async (productId, chargeId) => {
        setSourceProduct(productId);
        setSourceCharge(chargeId);

        if (productId && chargeId) {
            // Save to backend
            await updateCharge(activeCharge.id, {
                packaging_source_product_id: productId,
                packaging_source_charge_id: chargeId
            });
            // Refresh properties to show inherited ones
            fetchPackagingProperties(productData.sku, activeCharge.id);
        } else if (!productId) {
            // Clear source
            await updateCharge(activeCharge.id, {
                packaging_source_product_id: null,
                packaging_source_charge_id: null
            });
            fetchPackagingProperties(productData.sku, activeCharge.id);
        }
    };

    // Sorting
    const { items: sortedProperties, requestSort, sortConfig } = useSortableData(packagingProperties);

    const handleUpdateValue = (id, newValue) => {
        updatePackagingProperty(id, { value: newValue });
    };

    const handleKeyDown = (e, id, newValue) => {
        if (e.key === 'Enter') {
            e.target.blur(); // Triggers onBlur which calls handleUpdateValue
        }
    };

    const togglePropertySelection = (propId) => {
        setSelectedProperties(prev =>
            prev.includes(propId)
                ? prev.filter(id => id !== propId)
                : [...prev, propId]
        );
    };

    const handleAddSelected = async () => {
        for (const propId of selectedProperties) {
            const prop = availablePackagingProperties.find(p => p.id === propId);
            if (prop) {
                await addPackagingProperty({
                    packaging_field_id: prop.id,
                    name: prop.name,
                    value: '',
                    group_name: prop.group_name,
                    group_color: prop.group_color,
                    is_feature: false,
                    notes: prop.notes || ''
                });
            }
        }
        setIsAddModalOpen(false);
        setSelectedProperties([]);
        setSearchTerm('');
    };

    const handleCalculatePallet = () => {
        setPalletMiddleWidget('Pallet3DVisualizer');
        setPalletRightWidget('PalletControls');
        setIsPalletMode(true);
    };

    const handleLiveTracking = () => {
        setPalletMiddleWidget('LiveTrackingMap');
        setPalletRightWidget('LiveTrackingDetails');
        setIsPalletMode(true);
    };

    // Filter available properties: Exclude ALREADY PRESENT properties (both Local and Inherited)
    const filteredAvailableProperties = availablePackagingProperties
        .filter(ap => !packagingProperties.some(p => p.packaging_field_id === ap.id)) // Use packaging_field_id to match
        .filter(ap => ap.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const { items: sortedAvailableProperties, requestSort: requestSortAdd, sortConfig: sortConfigAdd } = useSortableData(filteredAvailableProperties);

    if (isPalletMode && (palletMiddleWidget === 'Pallet3DVisualizer' || palletMiddleWidget === 'Pallet2DVisualizer')) {
        return (
            <div className="h-full flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-white">Pallet Calculation Results</h2>
                        <p className="text-xs text-stone-500 mt-1">Select an arrangement to visualize it.</p>
                    </div>
                </div>
                <div className="flex-1 overflow-hidden">
                    <PalletResultsTable />
                </div>
            </div>
        );
    }

    if (isPalletMode && palletMiddleWidget === 'LiveTrackingMap') {
        return (
            <div className="h-full flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-white">Live Tracking Dashboard</h2>
                        <p className="text-xs text-stone-500 mt-1">Real-time shipment updates and documentation.</p>
                    </div>
                </div>
                <div className="flex-1 overflow-hidden">
                    <LiveTrackingDashboard />
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6 gap-4">
                <div>
                    <h2 className="text-xl font-bold text-white">Packaging Logistics</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage packaging specifications.</p>
                </div>

                {/* Inheritance Controls - Moved to Header */}
                <div className="flex-1 mx-4 flex gap-2 items-center">
                    <div className="flex-1 max-w-xs">
                        <SearchableSelect
                            options={accessoryProducts.filter(p => p.id !== productData?.id).map(p => ({ value: p.id, label: `${p.model_name || p.name || 'Unknown'} (${p.sku})` }))}
                            value={sourceProduct}
                            onChange={(val) => handleSourceChange(val, null)}
                            placeholder="Select Source Product..."
                            className="w-full"
                        />
                    </div>
                    <div className="flex-1 max-w-xs">
                        <SearchableSelect
                            options={availableSourceCharges.map(c => ({ value: c.id, label: `Batch: ${c.batch_number} (${c.status})` }))}
                            value={sourceCharge}
                            onChange={(val) => handleSourceChange(sourceProduct, val)}
                            placeholder="Select Source Charge..."
                            disabled={!sourceProduct}
                            className="w-full"
                        />
                    </div>
                    {sourceProduct && (
                        <button
                            onClick={() => handleSourceChange(null, null)}
                            className="h-[38px] px-3 rounded border border-red-900/50 text-red-500 hover:bg-red-900/20 transition flex items-center gap-2"
                            title="Unlink Source"
                        >
                            <Unlink size={16} />
                        </button>
                    )}
                </div>

                <div className="flex gap-3">
                    <GhostButton onClick={handleCalculatePallet}><Box size={16} /> Calculate Pallet</GhostButton>
                    <GhostButton onClick={handleLiveTracking}><Truck size={16} /> Live-Tracking</GhostButton>
                    {canEditContent && <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> Add Packaging Property</GhostButton>}
                    {canManageSettings && <GhostButton onClick={() => setIsManageModalOpen(true)}><Settings size={16} /> Manage Definitions</GhostButton>}
                </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10 bg-stone-950">
                            <tr>
                                <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Property" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Value" sortKey="value" currentSort={sortConfig} onSort={requestSort} className="w-[20%]" />

                                <SortableHeader label="Notes" sortKey="notes" currentSort={sortConfig} onSort={requestSort} />
                                <th className="text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedProperties.map(prop => {
                                const isInherited = prop.source === 'inherited';
                                return (
                                    <tr key={prop.id} className={`hover:bg-stone-800/30 transition-colors border-b border-stone-800 last:border-0 ${isInherited ? 'bg-stone-900/30' : ''}`}>
                                        <td className="px-4 py-0.5">
                                            <div className="flex items-center gap-2">
                                                <Badge color={prop.group_color || 'gray'}>{prop.group_name}</Badge>
                                                {isInherited && <span className="text-[10px] text-stone-500 border border-stone-700 px-1 rounded bg-stone-900">Inherited</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-300">{prop.name}</td>
                                        <td className="px-4 py-0.5">
                                            <div className="flex items-center gap-2">
                                                {prop.type_name === 'Boolean' ? (
                                                    <input
                                                        type="checkbox"
                                                        checked={prop.value === 'true'}
                                                        onChange={(e) => !isInherited && handleUpdateValue(prop.id, e.target.checked ? 'true' : 'false')}
                                                        className={`accent-stone-500 w-4 h-4 ${isInherited ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                        style={{ colorScheme: 'dark' }}
                                                        disabled={!canEditContent || isInherited}
                                                    />
                                                ) : (
                                                    <Input
                                                        defaultValue={prop.value}
                                                        onBlur={(e) => !isInherited && handleUpdateValue(prop.id, e.target.value)}
                                                        onKeyDown={(e) => !isInherited && handleKeyDown(e, prop.id, e.target.value)}
                                                        className={`bg-transparent border-transparent hover:border-stone-700 focus:bg-stone-950 h-7 py-0 text-sm w-1/2 ${isInherited ? 'text-stone-500 cursor-not-allowed' : ''}`}
                                                        disabled={!canEditContent || isInherited}
                                                    />
                                                )}
                                                {prop.unit && <span className="text-stone-500 text-xs">{prop.unit}</span>}
                                            </div>
                                        </td>

                                        <td className="px-4 py-0.5">
                                            <Input
                                                key={`${prop.id}-${prop.notes}-${prop.master_notes}`}
                                                defaultValue={(prop.notes && prop.notes !== 'Add notes...') ? prop.notes : (prop.master_notes || '')}
                                                onBlur={(e) => !isInherited && updatePackagingProperty(prop.id, { notes: e.target.value })}
                                                onKeyDown={(e) => !isInherited && handleKeyDown(e, prop.id, e.target.value)}
                                                className={`bg-transparent border-transparent hover:border-stone-700 focus:bg-stone-950 h-7 py-0 text-sm w-full ${isInherited ? 'text-stone-500 cursor-not-allowed' : ''}`}
                                                placeholder={isInherited ? "" : "Add notes..."}
                                                disabled={!canEditContent || isInherited}
                                            />
                                        </td>
                                        <td className="px-4 py-0.5 text-right">
                                            <div className="flex justify-end gap-2">
                                                {canEditContent && !isInherited && (
                                                    <button
                                                        onClick={() => deletePackagingProperty(prop.id)}
                                                        className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Modal - Smart Selection */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Packaging Properties" maxWidth="max-w-4xl">
                <div className="space-y-4 flex flex-col h-[500px]">
                    <Input
                        placeholder="Search available packaging properties..."
                        className="bg-stone-800 border-stone-600"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <div className="flex-1 overflow-hidden border border-stone-700 rounded-lg flex flex-col">
                        <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-4 px-3 py-2 bg-stone-950 text-stone-500 text-xs uppercase font-semibold border-b border-stone-800 sticky top-0 z-10">
                            <div className="w-4"></div>
                            <SortableHeader label="Property" sortKey="name" currentSort={sortConfigAdd} onSort={requestSortAdd} as="div" />
                            <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfigAdd} onSort={requestSortAdd} as="div" />
                            <SortableHeader label="Type" sortKey="type_name" currentSort={sortConfigAdd} onSort={requestSortAdd} className="text-right" as="div" />
                        </div>
                        <div className="overflow-y-auto flex-1 p-1 space-y-1">
                            {sortedAvailableProperties.map(ap => (
                                <div
                                    key={ap.id}
                                    onClick={() => togglePropertySelection(ap.id)}
                                    className={`grid grid-cols-[auto_1fr_1fr_auto] gap-4 items-center px-3 py-1 border rounded cursor-pointer transition select-none ${selectedProperties.includes(ap.id) ? 'bg-blue-900/20 border-blue-500/50' : 'bg-stone-800/30 border-stone-700 hover:bg-stone-800'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedProperties.includes(ap.id)}
                                        onChange={() => { }} // Handled by parent div click
                                        className="accent-stone-500 w-4 h-4 cursor-pointer"
                                    />
                                    <div className="text-sm text-stone-200">{ap.name}</div>
                                    <div><Badge color={ap.group_color} size="sm">{ap.group_name}</Badge></div>
                                    <div className="text-right text-xs text-stone-400">{ap.type_name}</div>
                                </div>
                            ))}
                            {sortedAvailableProperties.length === 0 && (
                                <div className="text-center text-stone-500 py-4">No new packaging properties available.</div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                        <GhostButton onClick={() => setIsAddModalOpen(false)}>Cancel</GhostButton>
                        <PrimaryButton onClick={handleAddSelected}>Add Selected ({selectedProperties.length})</PrimaryButton>
                    </div>
                </div>
            </Modal>

            {/* Management Modals */}
            <PackagingManagementModal
                isOpen={isManageModalOpen}
                onClose={() => {
                    setIsManageModalOpen(false);
                    if (productData?.sku && activeCharge?.id) {
                        fetchPackagingProperties(productData.sku, activeCharge.id);
                    }
                }}
            />
        </div >
    );
};
